import { createSelector } from 'reselect';

import {
    concat,
    find,
    forEach,
    includes,
    isEmpty,
    isNil,
    pipe,
    prop,
    propEq,
    reduce,
    reverse,
    sortBy,
    sum,
} from 'ramda';
import update from 'immutability-helper';
import Defaults from 'mangools-commons/dist/constants/Defaults';
import { ANY_LANGUAGE } from 'mangools-commons/dist/constants/Languages';
import Languages from 'mangools-commons/dist/constants/Languages';

import { ANNOUNCEMENT_EMPTY_MESSAGE } from 'reducers/data/announcementsReducer';
import { DEFAULT_COMPETITOR_ROWS_MAX_LIMIT, DEFAULT_RELATED_ROWS_MAX_LIMIT } from 'reducers/userReducer';

import ResultFilterService from 'services/ResultFilterService';
import ResultSorterService from 'services/ResultSorterService';
import {
    getClustersWithMappings,
    getClustersWithStatsForVisibleKeywords,
} from '../features/clustering/services/ClusteringIntegration';
import {
    currencySelector,
    currentKeywordIdSelector,
    filterActiveSelector,
    filterQuickSettingsSelector,
    filterSettingsSelector,
    keywordSourceResourceIdSelector,
    listPanelFilterSelector,
    relativeKdActiveSelector,
    selectedClusterSelector,
    selectedKeywordIdsSelector,
    sortingSettingsSelector,
} from 'selectors/uiSelectors';

import {
    competitorRowsLimitSelector,
    fakeResultsDisabledSelector,
    keywordSourceResourceTypeSelector,
    limitsFetchedSelector,
    relatedRowsLimitSelector,
    resultsDataSelector,
    selectedSearchVolumeTimeframeTypeSelector,
    topLevelAnnouncementMessageVisibilitySelector,
} from 'selectors/sharedSelectors';

import {
    defaultCompetitorLocationSelector,
    defaultCompetitorQuerySelector,
    defaultGoogleLanguageSelector,
    defaultGoogleLocationSelector,
    defaultGoogleQuerySelector,
    defaultSharedDataSubSourceSelector,
} from 'selectors/defaultsSelectors';

import { newUserMessageVisibilitySelector } from 'selectors/userSelectors';

import KeywordSourceTypes, { GOOGLE_TYPES } from 'constants/KeywordSourceTypes';
import DataSubSourceTypes from 'constants/DataSubSourceTypes';
import KeywordsByDomainTypes from 'constants/KeywordsByDomainTypes';
import searchVolumeTimeframeTypes from 'constants/SearchVolumeTimeframeTypes';
import { FEATURE_TYPES } from 'constants/SerpFeatureTypes';
import { urlDataLpsSelector } from 'selectors/urlDataSelectors';

import { KW_OPPORTUNITIES_TYPE } from 'mangools-react-components/src/constants/kwOpportunities';
import { COMPETITOR_SORTABLE_COLUMNS_ONLY_ON_FE } from 'constants/keywordColumns';
import { CLUSTERING_CONFIG } from '../features/clustering';

const filterByCluster = (keywords, selectedClusterName, language, allKeywords) => {
    if (!selectedClusterName || keywords.length === 0) {
        return keywords;
    }

    const languageCode = language?.code || 'en';
    const { keywordToCluster } = getClustersWithMappings(allKeywords, languageCode, {
        minWordLength: CLUSTERING_CONFIG.MIN_WORD_LENGTH,
        minNgramFrequency: CLUSTERING_CONFIG.MIN_NGRAM_FREQUENCY,
        minClusterSize: CLUSTERING_CONFIG.MIN_CLUSTER_SIZE,
        minTermLength: CLUSTERING_CONFIG.MIN_TERM_LENGTH,
        ngramSizes: CLUSTERING_CONFIG.NGRAM_SIZES,
        maxClusters: CLUSTERING_CONFIG.MAX_CLUSTERS,
    });

    return keywords.filter(keyword => {
        const clusterName = keywordToCluster.get(keyword.id);
        return clusterName === selectedClusterName;
    });
};

// Lists
export const fetchingListsDataSelector = state => state.data.lists.fetching;
const fetchingSerpResultsMoreSelector = state => state.data.serpResults.fetchingMore;
export const fetchingLoadMoreKeywordsByDomainResultsSelector = state => state.data.results.fetchingLoadMore;
const listsDataSelector = state => state.data.lists.data;
export const resultsDataCountSelector = state => state.data.results.data.length;
export const totalCountSelector = state => state.data.results.totalCount;
export const originalItemsCountSelector = state => state.data.results.originalItemsCount;

// Misc
export const rateLimitedSelector = state => state.data.results.rateLimited;
export const currentDataParamsSelector = state => state.data.results.currentDataParams;
export const isAllowedPaginationSelector = state => state.data.results.isAllowedPagination;
export const resultsLastPayloadSelector = state => state.data.results.lastPayload;

// Locations
export const locationsDataSelector = state => state.data.locations.data;
export const fetchingLocationsDataSelector = state => state.data.locations.fetching;

// Competitor selectors
export const competitorsSelector = state => state.data.competitors;
export const competitorsDataSelector = state => state.data.competitors.data;
export const fetchingCompetitorsDataSelector = state => state.data.competitors.fetching;
export const competitorsCurrentDataParamsSelector = createSelector(competitorsSelector, prop('currentDataParams'));

// Keyword Gap Results selectors
export const keywordGapResultsSelector = state => state.data.keywordGapResults;
export const keywordGapResultsDataSelector = createSelector(
    [keywordGapResultsSelector],
    (keywordGapResults) => {
        // Ensure keywordGapResults exists
        if (!keywordGapResults?.data) {
            return { keywords: [], competitors: [] };
        }

        const data = keywordGapResults.data;

        // Handle empty or null data
        if (!data || typeof data !== 'object') {
            return { keywords: [], competitors: [] };
        }

        // Get list of all competitors (domains)
        const competitors = Object.keys(data);
        const competitorsPrepared = competitors.reduce((acc, domain) => {
            acc[domain] = {};
            return acc;
        }, {});

        const competitorsBaseLine = competitors.reduce((acc, domain) => {
            acc[`competitor-${domain}-visits`] = null;
            acc[`competitor-${domain}-rank`] = null;
            return acc;
        }, {
            ['domain-visits']: null,
            ['domain-rank']: null,
            competitorRank: -1,
        });

        // Single pass: build keyword map with competitor-specific data
        const keywordMap = new Map();

        for (const [domain, result] of Object.entries(data)) {
            if (!result?.keywords?.length) {
                continue;
            }

            for (const keyword of result.keywords) {
                const keywordText = keyword.keyword; // Updated field name
                let keywordData = keywordMap.get(keywordText);

                if (!keywordData) {
                    keywordData = {
                        ...competitorsBaseLine,
                        competitorData: competitorsPrepared,
                        ...keyword,
                    };
                    keywordMap.set(keywordText, keywordData);
                }

                // Add competitor-specific data
                keywordData.competitorData[domain] = {
                    rank: keyword.serpPosition, // Updated field names
                    estimatedVisits: keyword.estimatedTraffic, // Updated field name
                    locationId: keyword.locationCode, // Updated field name
                };

                keywordData[`competitor-${domain}-visits`] = keyword.estimatedTraffic;
                keywordData[`competitor-${domain}-rank`] = keyword.serpPosition;

                // Update best competitor rank (SERP position)
                const currentSerpPosition = keyword.serpPosition;

                if (keywordData.competitorRank === -1) {
                    keywordData.competitorRank = currentSerpPosition;
                } else {
                    keywordData.competitorRank = Math.min(keywordData.competitorRank, currentSerpPosition);
                }

                // Ensure competitorRank is always a number (override any string values from API)
                keywordData.competitorRank = Number(keywordData.competitorRank) || 0;
            }
        }

        // Convert to array and sort by best competitor rank (ascending)
        const keywords = Array.from(keywordMap.values()).sort((a, b) => a.competitorRank - b.competitorRank);

        return { keywords, competitors };
    }
);
export const fetchingKeywordGapResultsDataSelector = state => state.data.keywordGapResults.fetching;
export const keywordGapResultsErrorSelector = state => state.data.keywordGapResults.error;
export const fetchingLoadMoreKeywordGapResultsDataSelector = state => state.data.keywordGapResults.fetchingLoadMore;
export const keywordGapResultsCurrentDataParamsSelector = createSelector(keywordGapResultsSelector, prop('currentDataParams'));
export const keywordGapResultsSortingSelector = createSelector(keywordGapResultsSelector, prop('sorting'));

// Currencies
export const allCurrenciesSelector = state => state.data.currencies.data;
export const currenciesFetchingSelector = state => state.data.currencies.fetching;
export const currenciesSelector = createSelector(
    [allCurrenciesSelector, currencySelector],
    (allCurrencies, selectedCurrency) => {
        if (!isNil(allCurrencies)) {
            const topCurrencyCodes = ['USD', 'GBP', 'EUR'].filter(
                currencyCode => currencyCode !== selectedCurrency.code,
            );
            // const topCurrencies = topCurrencyCodes
            //     .map(currencyCode => allCurrencies.find(currency => currency.code === currencyCode));
            const topCurrencies = allCurrencies.filter(currency => includes(currency.code, topCurrencyCodes));
            const currencies = allCurrencies.filter(
                currency => currency.code !== selectedCurrency.code && !includes(currency.code, topCurrencyCodes),
            );

            return [selectedCurrency, ...topCurrencies, ...currencies];
        } else {
            return null;
        }
    },
);

// Trends
const trendRawDataSelector = state => state.data.trends.data;
export const trendDataFetchingSelector = state => state.data.trends.fetching;
export const trendMetaDataSelector = state => state.data.trends.meta;

export const trendDataSelector = createSelector([trendRawDataSelector], data => {
    return data.filter(item => !isNil(item.value) || item.value === 0);
});

// Serps
export const serpResultsDataSelector = state => {
    if (state.data.serpResults.data.length > 0) {
        // TODO: remove MAP PACK filter once new UI is ready
        return state.data.serpResults.data.filter(
            item => ![FEATURE_TYPES.LOCAL_PACK].includes(item.type),
        );
    }

    return state.data.serpResults.data;
};
export const fetchingSerpResultsSelector = state => state.data.serpResults.fetching;
export const nextSerpPageSelector = state => state.data.serpResults.nextPage;

export const serpResultsLoadingSelector = createSelector(
    [fetchingSerpResultsSelector, fetchingSerpResultsMoreSelector],
    (fetching, fetchingMore) => fetching || fetchingMore,
);

// Competitor kw selectors

export const isCompetitorKeywordsSelector = createSelector(
    [keywordSourceResourceTypeSelector],
    keywordSource => keywordSource === KeywordSourceTypes.COMPETITOR,
);

// Create language lookup by code for efficiency
const LANGUAGES_BY_CODE = Languages.reduce((acc, lang) => {
    acc[lang.code] = lang;
    return acc;
}, {});

// Location to language mapping - static mapping object
const LOCATION_TO_LANGUAGE_MAP = {
    2804: LANGUAGES_BY_CODE['uk'], // Ukraine -> Ukrainian
    2276: LANGUAGES_BY_CODE['de'], // Germany -> German
    2250: LANGUAGES_BY_CODE['fr'], // France -> French
    2724: LANGUAGES_BY_CODE['es'], // Spain -> Spanish
    2380: LANGUAGES_BY_CODE['it'], // Italy -> Italian
    2528: LANGUAGES_BY_CODE['nl'], // Netherlands -> Dutch
    2620: LANGUAGES_BY_CODE['pt'], // Portugal -> Portuguese
    2703: LANGUAGES_BY_CODE['sk'], // Slovakia -> Slovak
    2203: LANGUAGES_BY_CODE['cs'], // Czechia -> Czech
    2208: LANGUAGES_BY_CODE['da'], // Denmark -> Danish
    2246: LANGUAGES_BY_CODE['fi'], // Finland -> Finnish
    2578: LANGUAGES_BY_CODE['nn'], // Norway -> Norwegian
    2752: LANGUAGES_BY_CODE['sv'], // Sweden -> Swedish
    2792: LANGUAGES_BY_CODE['tr'], // Turkey -> Turkish
    2392: LANGUAGES_BY_CODE['ja'], // Japan -> Japanese
    2458: LANGUAGES_BY_CODE['ms'], // Malaysia -> Malay
    2704: LANGUAGES_BY_CODE['vi'], // Vietnam -> Vietnamese
};

// Location to language mapping selector for competitor mode
export const locationToLanguageMappingSelector = createSelector(
    [currentDataParamsSelector, keywordSourceResourceTypeSelector],
    (currentDataParams, keywordSourceType) => {
        // Only apply mapping for competitor mode
        if (keywordSourceType !== KeywordSourceTypes.COMPETITOR) {
            return ANY_LANGUAGE;
        }

        const locationId = currentDataParams?.locationId;
        return LOCATION_TO_LANGUAGE_MAP[locationId] || ANY_LANGUAGE;
    }
);

export const competitorKeywordsCountsSelector = createSelector(
    [resultsDataSelector, keywordSourceResourceTypeSelector],
    (resultsData, keywordSource) => {
        if (keywordSource === KeywordSourceTypes.COMPETITOR) {
            const organicKeywords = resultsData.filter(item => item.keywordType === KeywordsByDomainTypes.ORGANIC);
            const paidKeywords = resultsData.filter(item => item.keywordType === KeywordsByDomainTypes.PAID);

            return {
                organic: organicKeywords.length,
                paid: paidKeywords.length,
            };
        } else {
            return {
                organic: 0,
                paid: 0,
            };
        }
    },
);

// More results selector

// NOTE: Returns true if there might be more keywords available.
// There is a possible false-positive when the amount of returned
// keywords == the keyword rows limit.
export const moreKeywordsAvailableSelector = createSelector(
    [
        fakeResultsDisabledSelector,
        filterActiveSelector,
        resultsDataSelector,
        competitorRowsLimitSelector,
        relatedRowsLimitSelector,
        keywordSourceResourceTypeSelector,
        defaultSharedDataSubSourceSelector,
    ],
    (fakeResultsDisabled, filterActive, data, competitorRows, relatedRows, keywordSource, subSource) => {
        if (fakeResultsDisabled === true || filterActive === true) {
            return false;
        } else if (keywordSource === KeywordSourceTypes.COMPETITOR) {
            let filtered = [];

            if (subSource === DataSubSourceTypes.COMPETITORS_ORGANIC) {
                filtered = data.filter(item => item.keywordType === KeywordsByDomainTypes.ORGANIC);
            } else {
                filtered = data.filter(item => item.keywordType === KeywordsByDomainTypes.PAID);
            }

            return filtered.length === competitorRows && competitorRows < DEFAULT_COMPETITOR_ROWS_MAX_LIMIT;
        } else if (includes(keywordSource, GOOGLE_TYPES)) {
            return data.length === relatedRows && relatedRows < DEFAULT_RELATED_ROWS_MAX_LIMIT;
        } else {
            return false;
        }
    },
);

// Result data selectors, filtered/sorted/in lists/etc.

// NOTE: Creates a memoized map of all keywords in all lists
// for easy O(1) check if kw is in some list.
// Map example: {
//     kwid01: [ { id: asndsa, name: 'listName' }, { id: dsads, name: 'listName2' } ]
//     ...
// }

export const keywordsGapInListsMapSelector = createSelector(
    [listsDataSelector, keywordGapResultsDataSelector],
    (lists, data) => {
        const keywords = data.keywords.reduce((kwIdMap, kw) => {
            // eslint-disable-next-line no-param-reassign
            kwIdMap[kw.id] = 1;
            return kwIdMap;
        }, {});

        const kwListMap = reduce(
            (map, val) => {
                const newMap = { ...map };
                const listId = val.id;
                const listName = val.name;
                const kwIds = val.keywordIds;

                // For each keyword ID in set object key
                // and a value is an array of list objects in which that kw in contained
                forEach(kwId => {
                    if (keywords[kwId] === 1) {
                        if (isNil(newMap[kwId])) {
                            newMap[kwId] = [
                                {
                                    // eslint-disable-line no-param-reassign
                                    id: listId,
                                    name: listName,
                                },
                            ];
                        } else {
                            newMap[kwId] = concat(newMap[kwId], [
                                {
                                    // eslint-disable-line no-param-reassign
                                    id: listId,
                                    name: listName,
                                },
                            ]);
                        }
                    }
                }, kwIds);

                return newMap;
            },
            {},
            lists,
        );

        return kwListMap;
    },
);

export const keywordsInListsMapSelector = createSelector(
    [listsDataSelector, resultsDataSelector],
    (lists, keywords) => {
        // eslint-disable-next-line no-param-reassign
        keywords = keywords.reduce((kwIdMap, kw) => {
            // eslint-disable-next-line no-param-reassign
            kwIdMap[kw.id] = 1;
            return kwIdMap;
        }, {});

        const kwListMap = reduce(
            (map, val) => {
                const newMap = { ...map };
                const listId = val.id;
                const listName = val.name;
                const kwIds = val.keywordIds;

                // For each keyword ID in set object key
                // and a value is an array of list objects in which that kw in contained
                forEach(kwId => {
                    if (keywords[kwId] === 1) {
                        if (isNil(newMap[kwId])) {
                            newMap[kwId] = [
                                {
                                    // eslint-disable-line no-param-reassign
                                    id: listId,
                                    name: listName,
                                },
                            ];
                        } else {
                            newMap[kwId] = concat(newMap[kwId], [
                                {
                                    // eslint-disable-line no-param-reassign
                                    id: listId,
                                    name: listName,
                                },
                            ]);
                        }
                    }
                }, kwIds);

                return newMap;
            },
            {},
            lists,
        );

        return kwListMap;
    },
);


// content types
export const contentTypeRootSelector = state => state.data.contentTypes;
export const fetchingContentTypesSelector = createSelector(
    [contentTypeRootSelector], contentTypeState => contentTypeState.fetching
);

export const dataContentTypesSelector = createSelector(
    [contentTypeRootSelector], contentTypeState => contentTypeState.data
);

// serp features
export const serpFeaturesRootSelector = state => state.data.serpFeatures
export const fetchingSerpFeaturesSelector = createSelector(
    [serpFeaturesRootSelector], serpFeaturesState => serpFeaturesState?.fetching
);
export const fetchingSerpFeaturesByKeywordSelector = (keywordId) => createSelector(
    [serpFeaturesRootSelector], serpFeaturesState => serpFeaturesState?.fetchingIds.includes(keywordId)
);
export const serpFeaturesErrorSelector = createSelector(
    [serpFeaturesRootSelector], serpFeaturesState => serpFeaturesState?.error
);
export const dataSerpFeaturesSelector = (keywordId) => createSelector(
    [serpFeaturesRootSelector], serpFeaturesState => serpFeaturesState?.data?.[keywordId] ?? null
);

export const dataAllSerpFeaturesSelector = createSelector(
    [serpFeaturesRootSelector],
    (serpFeaturesState) => serpFeaturesState?.data || {}
);

// search intent
export const searchIntentRootSelector = state => state.data.searchIntent;
export const fetchingSearchIntentSelector = createSelector(
    [searchIntentRootSelector], searchIntentState => searchIntentState?.fetching
);
export const fetchingSearchIntentByKeywordSelector = (keywordId) => createSelector(
    [searchIntentRootSelector], searchIntentState => searchIntentState?.fetchingIds.includes(keywordId)
);
export const searchIntentErrorSelector = createSelector(
    [searchIntentRootSelector], searchIntentState => searchIntentState?.data
);

export const dataAllSearchIntentsSelector = createSelector(
    [searchIntentRootSelector],
    (searchIntentState) => searchIntentState?.data?.data || {}
);

export const dataSearchIntentSelector = (keywordId) => createSelector(
    [dataAllSearchIntentsSelector],
    (data) => data?.[keywordId] || []
);

export const kwOpportunitiesRootSelector = state => state.data.kwOpportunities;
export const fetchingKwOpportunitiesSelector = createSelector(
    [kwOpportunitiesRootSelector], kwOpportunitiesState => kwOpportunitiesState?.fetching
);

export const fetchingKwOpportunitiesByKeywordSelector = (keywordId) => createSelector(
    [kwOpportunitiesRootSelector], kwOpportunitiesState => kwOpportunitiesState?.fetchingIds.includes(keywordId)
);

export const kwOpportunitiesErrorSelector = createSelector(
    [kwOpportunitiesRootSelector], kwOpportunitiesState => kwOpportunitiesState?.data
);

export const dataAllKwOpportunitiesSelector = createSelector(
    [kwOpportunitiesRootSelector],
    (kwOpportunitiesState) => kwOpportunitiesState?.data || {}
);

export const dataKwOpportunitiesSelector = (keywordId) => createSelector(
    [kwOpportunitiesRootSelector],
    (data) => {
        return data.data?.[keywordId] || null
    }
);

export const filteredAndSortedResultsDataWithoutClusterSelector = createSelector(
    [
        resultsDataSelector,
        keywordsInListsMapSelector,
        filterActiveSelector,
        filterSettingsSelector,
        sortingSettingsSelector,
        keywordSourceResourceTypeSelector,
        defaultSharedDataSubSourceSelector,
        filterQuickSettingsSelector,
        currencySelector,
        selectedSearchVolumeTimeframeTypeSelector,
        urlDataLpsSelector,
        relativeKdActiveSelector,
        dataContentTypesSelector,
        dataAllSearchIntentsSelector,
        dataAllKwOpportunitiesSelector,
        dataAllSerpFeaturesSelector,
    ],
    (
        resultsData,
        keywordsInListsMap,
        filterActive,
        filterSettings,
        sortingSettings,
        keywordSource,
        dataSubSource,
        quickSettings,
        currency,
        selectedSearchVolumeTimeframe,
        urlDataLps,
        relativeKdActive,
        contentTypes,
        searchIntent,
        kwOpportunities,
        serpFeatures,
    ) => {
        let data = resultsData;
        let isWithFrontendSorting = true;

        if (keywordSource === KeywordSourceTypes.COMPETITOR) {
            if (!COMPETITOR_SORTABLE_COLUMNS_ONLY_ON_FE.includes(sortingSettings.column)) {
                isWithFrontendSorting = false;
            }
            if (dataSubSource === DataSubSourceTypes.COMPETITORS_ORGANIC) {
                data = data.filter(item => item.keywordType === KeywordsByDomainTypes.ORGANIC);
            } else if (dataSubSource === DataSubSourceTypes.COMPETITORS_PAID) {
                data = data.filter(item => item.keywordType === KeywordsByDomainTypes.PAID);
            }
        }

        const searchVolumeTF =
            keywordSource === KeywordSourceTypes.COMPETITOR
                ? searchVolumeTimeframeTypes.LAST_12_MONTHS
                : selectedSearchVolumeTimeframe;

        const filteredData =
            data.length === 0
                ? data
                : ResultFilterService.filter({
                    data,
                    keywordsInListsMap,
                    settings: filterSettings,
                    keywordSource,
                    quickSettings,
                    filterActive,
                    currency,
                    selectedSearchVolumeTimeframe: searchVolumeTF,
                    urlDataLps,
                    relativeKdActive,
                });

        let dataToFinal = filteredData;

        if (isWithFrontendSorting) {
            dataToFinal = ResultSorterService.sort({
                data: filteredData,
                settings: sortingSettings,
                selectedSearchVolumeTimeframe: searchVolumeTF,
            });
        }

        let finalData = dataToFinal.map((item) => {
            return {
                ...item,
                contentTypes: contentTypes[item.id],
                searchIntent: searchIntent[item.id],
                serpFeatures: serpFeatures[item.id],
                kwOpps: kwOpportunities[item.id],
            }
        })

        if (filterActive) {
            // We want to filter data after theirs joining, but we don't want to join data before initial filtering
            if (filterSettings.includedContentTypes.length > 0) {
                const filterValues = new Set(filterSettings.includedContentTypes.map(({ value }) => value));

                finalData = finalData.filter((d) => {
                    const hasSearch = filterValues.has("SEARCH") && d.contentTypes === undefined;
                    const hasUnknown = filterValues.has("UNKNOWN") && d.contentTypes?.length === 0;
                    const hasMatchingType = d.contentTypes?.some((type) => filterValues.has(type));

                    return hasSearch || hasUnknown || hasMatchingType;
                });
            }

            if (filterSettings.includedSearchIntentTypes.length !== 0) {
                const filterValues = filterSettings.includedSearchIntentTypes.map(({ value }) => value)
                finalData = finalData.filter((d) =>
                    (d.searchIntent ?? []).some((type) => filterValues.includes(type.toUpperCase()))
                );
            }

            if ((filterSettings.includedKwOpportunities ?? []).length !== 0) {
                const filterValues = new Set(filterSettings.includedKwOpportunities.map(({ value }) => value));
                finalData = finalData.filter((d) => {
                    const hasOops = d.kwOpps !== undefined
                    const opps = d.kwOpps ?? {};
                    const hasSearch = filterValues.has("SEARCH") && !hasOops

                    const hasNoneOpps = hasOops && filterValues.has(KW_OPPORTUNITIES_TYPE.NONE) && !opps.isOutdatedPage && (opps.missingWordsInTitle ?? []).length === 0
                    const hasOutdated = hasOops && filterValues.has(KW_OPPORTUNITIES_TYPE.OUTDATED) && opps.isOutdatedPage
                    const hasMissingWordsInTitle = hasOops && filterValues.has(KW_OPPORTUNITIES_TYPE.MISSING_WORDS) && (opps.missingWordsInTitle ?? []).length !== 0

                    return (
                        hasSearch ||
                        hasMissingWordsInTitle ||
                        hasOutdated ||
                        hasNoneOpps
                    );
                });
            }

            if (filterSettings.includedSerpFeatures.length > 0) {
                const filterValues = new Set(filterSettings.includedSerpFeatures.map(({ value }) => value));

                finalData = finalData.filter((d) => {
                    const hasSearch = filterValues.has("SEARCH") && d.serpFeatures === undefined;
                    const hasNone = filterValues.has("NONE") && d.serpFeatures?.length === 0;
                    const hasMatchingType = d.serpFeatures?.some((type) => filterValues.has(type));

                    return hasSearch || hasNone || hasMatchingType
                });
            }
        }

        return finalData;
    }
);

export const filteredAndSortedResultsDataSelector = createSelector(
    [
        filteredAndSortedResultsDataWithoutClusterSelector,
        selectedClusterSelector,
        defaultGoogleLanguageSelector,
        resultsDataSelector,
    ],
    (
        filteredDataWithoutCluster,
        selectedCluster,
        language,
        allResultsData,
    ) => {
        if (selectedCluster) {
            return filterByCluster(filteredDataWithoutCluster, selectedCluster, language, allResultsData);
        }

        return filteredDataWithoutCluster;
    }
);

export const clusteringStatsSelector = createSelector(
    [
        resultsDataSelector,
        filteredAndSortedResultsDataWithoutClusterSelector,
        defaultGoogleLanguageSelector,
        keywordSourceResourceTypeSelector,
        selectedSearchVolumeTimeframeTypeSelector,
    ],
    (
        allKeywords,
        filteredKeywords,
        language,
        keywordSourceType,
        selectedSearchVolumeTimeframe,
    ) => {
        if (!allKeywords || allKeywords.length === 0) {
            return {
                clusters: [],
                totalStats: { keywords: 0, searches: 0, difficulty: null },
                hasEnoughKeywords: false
            };
        }

        const hasEnoughKeywords = allKeywords.length >= 3;

        if (!hasEnoughKeywords) {
            return {
                clusters: [],
                totalStats: { keywords: filteredKeywords.length, searches: 0, difficulty: null },
                hasEnoughKeywords: false
            };
        }

        const languageCode = language?.code || 'en';

        try {
            const result = getClustersWithStatsForVisibleKeywords(
                allKeywords,
                filteredKeywords,
                languageCode,
                {
                    minWordLength: CLUSTERING_CONFIG.MIN_WORD_LENGTH,
                    minNgramFrequency: CLUSTERING_CONFIG.MIN_NGRAM_FREQUENCY,
                    minClusterSize: CLUSTERING_CONFIG.MIN_CLUSTER_SIZE,
                    minTermLength: CLUSTERING_CONFIG.MIN_TERM_LENGTH,
                    ngramSizes: CLUSTERING_CONFIG.NGRAM_SIZES,
                    maxClusters: CLUSTERING_CONFIG.MAX_CLUSTERS,
                },
                keywordSourceType,
                selectedSearchVolumeTimeframe
            );

            const keywordsWithKD = filteredKeywords.filter(kw => kw.rank && kw.rank >= 1 && kw.rank <= 100);
            const avgDifficulty = keywordsWithKD.length > 0
                ? Math.round(keywordsWithKD.reduce((sum, kw) => sum + (kw.rank || 0), 0) / keywordsWithKD.length)
                : null;

            const totalSearches = filteredKeywords.reduce((sum, kw) => {
                const searchVolume = keywordSourceType === 'COMPETITOR'
                    ? (kw.searchVolumesByTimeframe)?.LAST_12_MONTHS || 0
                    : (kw.searchVolumesByTimeframe)?.[selectedSearchVolumeTimeframe] || 0;
                return sum + Math.max(searchVolume, 0); // NO_VALUE (-1) adds nothing
            }, 0);

            return {
                clusters: result.clusters || [],
                totalStats: {
                    keywords: filteredKeywords.length,
                    searches: totalSearches,
                    difficulty: avgDifficulty
                },
                hasEnoughKeywords: true
            };
        } catch (error) {
            const keywordsWithKD = filteredKeywords.filter(kw => kw.rank && kw.rank >= 1 && kw.rank <= 100);
            const avgDifficulty = keywordsWithKD.length > 0
                ? Math.round(keywordsWithKD.reduce((sum, kw) => sum + (kw.rank || 0), 0) / keywordsWithKD.length)
                : null;

            const totalSearches = filteredKeywords.reduce((sum, kw) => {
                const searchVolume = keywordSourceType === 'COMPETITOR'
                    ? (kw.searchVolumesByTimeframe)?.LAST_12_MONTHS || 0
                    : (kw.searchVolumesByTimeframe)?.[selectedSearchVolumeTimeframe] || 0;
                return sum + Math.max(searchVolume, 0); // NO_VALUE (-1) adds nothing
            }, 0);

            return {
                clusters: [],
                totalStats: {
                    keywords: filteredKeywords.length,
                    searches: totalSearches,
                    difficulty: avgDifficulty
                },
                hasEnoughKeywords: true
            };
        }
    }
);

export const filteredAndSortedSelectedKeywordIdsSelector = createSelector(
    [selectedKeywordIdsSelector, filteredAndSortedResultsDataSelector],
    (selectedIds, filteredAndSortedData) => {
        const keywordsIds = filteredAndSortedData.map(item => item.id);

        const selectedIdsObj = reduce(
            (obj, val) => {
                obj[val] = true; // eslint-disable-line no-param-reassign
                return obj;
            },
            {},
            selectedIds,
        );

        return keywordsIds.filter(id => selectedIdsObj[id] !== undefined);
    },
);

export const filteredAndSortedSelectedKeywordsSelector = createSelector(
    [selectedKeywordIdsSelector, filteredAndSortedResultsDataSelector],
    (ids, sortedAndFilteredKeywords) => sortedAndFilteredKeywords.filter(kw => includes(kw.id, ids)),
);

export const filteredAndSortedSelectedKeywordStringsSelector = createSelector(
    [filteredAndSortedSelectedKeywordsSelector],
    selectedKeywords => selectedKeywords.map(kw => kw.keyword),
);

export const filteredAndSortedSelectedKeywordsSearchCountTotalSelector = createSelector(
    [
        filteredAndSortedSelectedKeywordsSelector,
        selectedSearchVolumeTimeframeTypeSelector,
        keywordSourceResourceTypeSelector,
    ],
    (keywords, selectedSearchVolumeTimeframe, keywordSourceType) => {
        return reduce(
            (count, { searchVolumesByTimeframe }) => {
                let selectedSearchVolumeTimeframeCount;

                if (keywordSourceType === KeywordSourceTypes.COMPETITOR) {
                    selectedSearchVolumeTimeframeCount = prop(
                        searchVolumeTimeframeTypes.LAST_12_MONTHS,
                        searchVolumesByTimeframe,
                    );
                } else {
                    selectedSearchVolumeTimeframeCount = prop(selectedSearchVolumeTimeframe, searchVolumesByTimeframe);
                }

                if (selectedSearchVolumeTimeframeCount > 0) {
                    return count + selectedSearchVolumeTimeframeCount;
                } else {
                    return count;
                }
            },
            0,
            keywords,
        );
    },
);


export const filteredAndSortedSelectedKeywordsAvgRankSelector = createSelector(
    [filteredAndSortedSelectedKeywordsSelector],
    keywords => {
        const withRank = keywords.filter(kw => kw.rank > 0);
        const ranks = withRank.map(kw => kw.rank);

        if (withRank.length > 0) {
            return sum(ranks) / withRank.length;
        } else {
            return 0;
        }
    },
);

export const filteredAndSortedSelectedKeywordsAvgCpcSelector = createSelector(
    [filteredAndSortedSelectedKeywordsSelector],
    keywords => {
        const withCpc = keywords.filter(kw => kw.cpc > 0);
        const cpcs = withCpc.map(kw => kw.cpc);

        if (withCpc.length > 0) {
            return sum(cpcs) / withCpc.length;
        } else {
            return 0;
        }
    },
);

export const filteredAndSortedSelectedKeywordsAvgPpcSelector = createSelector(
    [filteredAndSortedSelectedKeywordsSelector],
    keywords => {
        const withPpc = keywords.filter(kw => kw.ppc > 0);
        const ppcs = withPpc.map(kw => kw.ppc);

        if (withPpc.length > 0) {
            return sum(ppcs) / withPpc.length;
        } else {
            return 0;
        }
    },
);

export const filteredAndSortedSelectedKeywordsEtTotalSelector = createSelector(
    [filteredAndSortedSelectedKeywordsSelector],
    keywords => {
        const filteredKeywords = keywords.filter(kw => kw.estimatedTraffic > 0);
        return sum(filteredKeywords.map(kw => kw.estimatedTraffic));
    },
);

export const filteredAndSortedSelectedKeywordsAvgSpSelector = createSelector(
    [filteredAndSortedSelectedKeywordsSelector],
    keywords => {
        const withSp = keywords.filter(kw => kw.serpPosition > 0);
        const values = withSp.map(kw => kw.serpPosition);

        if (withSp.length > 0) {
            return sum(values) / withSp.length;
        } else {
            return 0;
        }
    },
);

export const allKeywordsSearchCountTotalSelector = createSelector(
    [resultsDataSelector, selectedSearchVolumeTimeframeTypeSelector, keywordSourceResourceTypeSelector],
    (keywords, selectedSearchVolumeTimeframe, keywordSourceType) => {
        return reduce(
            (count, { searchVolumesByTimeframe }) => {
                let selectedSearchVolumeTimeframeCount;

                if (keywordSourceType === KeywordSourceTypes.COMPETITOR) {
                    selectedSearchVolumeTimeframeCount = prop(
                        searchVolumeTimeframeTypes.LAST_12_MONTHS,
                        searchVolumesByTimeframe,
                    );
                } else {
                    selectedSearchVolumeTimeframeCount = prop(selectedSearchVolumeTimeframe, searchVolumesByTimeframe);
                }

                if (selectedSearchVolumeTimeframeCount > 0) {
                    return count + selectedSearchVolumeTimeframeCount;
                } else {
                    return count;
                }
            },
            0,
            keywords,
        );
    },
);

export const allResultsDataSelector = createSelector([resultsDataSelector, keywordGapResultsDataSelector], (resultsData, keywordGapResultsData) => {
    return [...resultsData, ...keywordGapResultsData.keywords];
});


export const allKeywordsAvgRankSelector = createSelector([resultsDataSelector], keywords => {
    const withRank = keywords.filter(kw => kw.rank > 0);
    const ranks = withRank.map(kw => kw.rank);

    if (withRank.length > 0) {
        return sum(ranks) / withRank.length;
    } else {
        return 0;
    }
});

export const allKeywordsAvgCpcSelector = createSelector([resultsDataSelector], keywords => {
    const withCpc = keywords.filter(kw => kw.cpc > 0);
    const cpcs = withCpc.map(kw => kw.cpc);

    if (withCpc.length > 0) {
        return sum(cpcs) / withCpc.length;
    } else {
        return 0;
    }
});

export const allKeywordsAvgPpcSelector = createSelector([resultsDataSelector], keywords => {
    const withPpc = keywords.filter(kw => kw.ppc > 0);
    const ppcs = withPpc.map(kw => kw.ppc);

    if (withPpc.length > 0) {
        return sum(ppcs) / withPpc.length;
    } else {
        return 0;
    }
});

export const allKeywordsEstimatedTrafficTotalSelector = createSelector([resultsDataSelector], keywords => {
    const filteredKeywords = keywords.filter(kw => kw.estimatedTraffic > 0);
    return sum(filteredKeywords.map(kw => kw.estimatedTraffic));
});

export const allKeywordsAvgSerpPositionSelector = createSelector([resultsDataSelector], keywords => {
    const withSp = keywords.filter(kw => kw.serpPosition > 0);
    const values = withSp.map(kw => kw.serpPosition);

    if (withSp.length > 0) {
        return sum(values) / withSp.length;
    } else {
        return 0;
    }
});

export const allKeywordIdsSelector = createSelector([resultsDataSelector], keywords => keywords.map(kw => kw.id));

// Current keyword id based selectors

export const currentKeywordSelector = createSelector(
    [currentKeywordIdSelector, allResultsDataSelector],
    (id, resultsData) => {
        if (!id) {
            return null;
        }
        const result = find(propEq('id', id))(resultsData);
        return isNil(result) ? null : result;
    },
);

export const keywordStringSelector = createSelector(
    [currentKeywordIdSelector, allResultsDataSelector],
    (id, resultsData) => {
        if (!id) {
            return null;
        }
        const result = find(propEq('id', id))(resultsData);
        return isNil(result) ? null : result.keyword;
    },
);

export const keywordSearchVolumesSelector = createSelector(
    [currentKeywordIdSelector, allResultsDataSelector],
    (id, resultsData) => {
        if (!id) {
            return [];
        }
        const result = find(propEq('id', id))(resultsData);
        return isNil(result) ? [] : result.monthlySearchVolumes;
    },
);

export const keywordLatestSearchVolumeSelector = createSelector(
    [currentKeywordIdSelector, allResultsDataSelector],
    (id, resultsData) => {
        if (!id) {
            return null;
        }
        const result = find(propEq('id', id))(resultsData);
        if (isNil(result) || isNil(result.searchCountLatest)) {
            return null;
        } else {
            return result.searchCountLatest;
        }
    },
);

export const keywordLanguageSelector = createSelector(
    [currentKeywordIdSelector, allResultsDataSelector, keywordSourceResourceTypeSelector, defaultGoogleLanguageSelector],
    (id, resultsData, keywordSourceType, defaultGoogleLanguage) => {
        const result = find(propEq('id', id))(resultsData);

        if (isNil(result)) {
            if (keywordSourceType === KeywordSourceTypes.COMPETITOR) {
                return ANY_LANGUAGE;
            } else {
                return defaultGoogleLanguage;
            }
        } else {
            return result.language;
        }
    },
);

export const keywordLocationSelector = createSelector(
    [
        currentKeywordIdSelector,
        allResultsDataSelector,
        keywordSourceResourceTypeSelector,
        defaultGoogleLocationSelector,
        defaultCompetitorLocationSelector,
    ],
    (id, resultsData, keywordSourceType, defaultGoogleLocation, defaultCompetitorLocation) => {
        const result = find(propEq('id', id))(resultsData);

        if (isNil(result)) {
            if (keywordSourceType === KeywordSourceTypes.COMPETITOR) {
                return defaultCompetitorLocation;
            } else {
                return defaultGoogleLocation;
            }
        } else {
            return result.location;
        }
    },
);

export const rankSelector = createSelector([currentKeywordIdSelector, allResultsDataSelector], (id, resultsData) => {
    const result = find(propEq('id', id))(resultsData);
    return isNil(result) ? Defaults.NO_VALUE : result.rank;
});

export const msvTrendsSelector = createSelector([currentKeywordIdSelector, allResultsDataSelector], (id, resultsData) => {
    const result = find(propEq('id', id))(resultsData);
    return isNil(result) ? [] : result.msvTrends;
});

export const noVolumeReportedSinceSelector = createSelector(
    [currentKeywordIdSelector, allResultsDataSelector],
    (id, resultsData) => {
        const result = find(propEq('id', id))(resultsData);
        return isNil(result) ? null : result.noVolumeReportedSince;
    },
);

export const searchCountSelector = createSelector(
    [
        currentKeywordIdSelector,
        allResultsDataSelector,
        selectedSearchVolumeTimeframeTypeSelector,
        keywordSourceResourceTypeSelector,
    ],
    (id, resultsData, selectedSearchVolumeTimeframe, keywordSourceType) => {
        return pipe(find(propEq('id', id)), keyword => {
            if (isNil(keyword)) {
                return 0;
            } else if (keywordSourceType === KeywordSourceTypes.COMPETITOR || keywordSourceType === KeywordSourceTypes.KEYWORD_GAP) {
                return keyword.searchVolumesByTimeframe[searchVolumeTimeframeTypes.LAST_12_MONTHS];
            } else {
                return keyword.searchVolumesByTimeframe[selectedSearchVolumeTimeframe];
            }
        })(resultsData);
    },
);

// List selectors

export const sortedListDataSelector = createSelector([listsDataSelector], lists => {
    const sorted = sortBy(prop('updatedAt'))(lists);
    return reverse(sorted);
});

export const filteredAndSortedListPanelDataSelector = createSelector(
    [sortedListDataSelector, listPanelFilterSelector],
    (lists, listNameFilter) => {
        if (isNil(listNameFilter) || listNameFilter === '') {
            return lists;
        } else {
            const sanitizedFilter = listNameFilter.trim().toLowerCase();
            return lists.filter(({ name }) => name.toLowerCase().includes(sanitizedFilter));
        }
    },
);

// Current list selectors

export const listKeywordIdsSelector = (state, listId) => {
    const lists = state.data.lists.data;
    const list = find(propEq('id', listId))(lists);

    return !isNil(list) ? list.keywordIds : [];
};

export const listSelector = (state, listId) => {
    const lists = state.data.lists.data;

    return find(propEq('id', listId))(lists);
};

export const lastUsedOrCreatedListSelector = createSelector([listsDataSelector], lists => {
    if (!isEmpty(lists)) {
        const sorted = sortBy(prop('updatedAt'))(lists);
        return reverse(sorted)[0];
    } else {
        return null;
    }
});

export const currentListDetailDataSelector = createSelector(
    [listsDataSelector, keywordSourceResourceIdSelector, keywordSourceResourceTypeSelector],
    (lists, listId, keywordSourceType) => {
        if (keywordSourceType === KeywordSourceTypes.LIST) {
            return find(propEq('id', listId))(lists);
        } else {
            return null;
        }
    },
);

export const currentListDetailNameSelector = createSelector([currentListDetailDataSelector], list => {
    if (!isNil(list)) {
        return list.name;
    } else {
        return null;
    }
});

export const currentListDetailIdSelector = createSelector([currentListDetailDataSelector], list => {
    if (!isNil(list)) {
        return list.id;
    } else {
        return null;
    }
});

// Announcements
const announcementsDataSelector = state => state.data.announcements.data;
const fetchingAnnouncementsDataSelector = state => state.data.announcements.fetching;

export const announcementsMessageDataSelector = createSelector([announcementsDataSelector], announcementsData => {
    // TODO: Remove after debugging
    // return {
    //     activeFrom: 0,
    //     activeTo: 0,
    //     btnText: 'Read more',
    //     icon: 'rocket',
    //     messageText: 'We\'ve launched the competitor analysis in KWFinder',
    //     newTab: false,
    //     url: 'http://mangools.com/blog'
    // };

    if (isNil(announcementsData)) {
        return ANNOUNCEMENT_EMPTY_MESSAGE;
    } else {
        return announcementsData;
    }
});

export const announcementMesageVisibilitySelector = createSelector(
    [
        fetchingAnnouncementsDataSelector,
        announcementsMessageDataSelector,
        limitsFetchedSelector,
        topLevelAnnouncementMessageVisibilitySelector,
        newUserMessageVisibilitySelector,
    ],
    (fetchingAnnouncements, announcementsMessageData, fetchedLimits, announcementsVisible, newUserMessageVisible) => {
        if (
            fetchedLimits === true &&
            fetchingAnnouncements === false &&
            announcementsVisible === true &&
            newUserMessageVisible === false
        ) {
            // TODO: Remove after debugging
            // return true;

            // There is an announcement which is active
            if (announcementsMessageData.activeTo > Date.now() / 1000) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    },
);

export const exportSelectedKeywordsFilenameSelector = createSelector(
    [
        keywordSourceResourceTypeSelector,
        defaultGoogleQuerySelector,
        defaultCompetitorQuerySelector,
        currentListDetailNameSelector,
    ],
    (kwSourceType, googleQuery, competitorQuery, listName) => {
        if (GOOGLE_TYPES.includes(kwSourceType)) {
            return googleQuery;
        } else if (kwSourceType === KeywordSourceTypes.COMPETITOR) {
            return competitorQuery;
        } else if (kwSourceType === KeywordSourceTypes.LIST) {
            return listName;
        } else if (kwSourceType === KeywordSourceTypes.IMPORT) {
            return 'import';
        } else {
            return '';
        }
    },
);

export const newCurrentKwIdSelector = createSelector(
    [filteredAndSortedSelectedKeywordIdsSelector, allKeywordIdsSelector],
    (selectedKwIds, allKeywordIds) => {
        const keywordIds = allKeywordIds.filter(id => !selectedKwIds.includes(id));

        return keywordIds.length === 0 ? null : keywordIds[0];
    },
);

export const allKeywordsSelectedSelector = createSelector(
    [filteredAndSortedResultsDataSelector, selectedKeywordIdsSelector],
    (data, selectedKwIds) => data.length > 0 && data.length === selectedKwIds.length,
);
// history

export const historyDataSelector = state => {
    return state.data.history.data.map((entry, index) => {
        return update(entry, { $merge: { key: `${entry.id}-${index}` } });
    });
};

export const fetchingSerpwatcherTrackingsSelector = state => state.data.serpwatcherTrackings.fetching;
export const serpwatcherTrackingsDataSelector = state => {
    return {
        swTrackingsById: state.data.serpwatcherTrackings.data.byId,
        swTrackingsAllIds: state.data.serpwatcherTrackings.data.allIds,
    };
};

export const getFirstRowFromDataResultsSelector = createSelector([resultsDataSelector], resultsData => resultsData[0]);

export const getKdHistoryDataSelector = createSelector(
    [state => state.data.kdHistory.data],
    kdHistoryData => kdHistoryData,
);

// Keyword Gap Results filtered and sorted data
export const filteredAndSortedKeywordGapResultsDataSelector = createSelector(
    [
        keywordGapResultsDataSelector,
        keywordsGapInListsMapSelector,
        filterActiveSelector,
        filterSettingsSelector,
        keywordGapResultsSortingSelector,
        currencySelector,
        urlDataLpsSelector,
        relativeKdActiveSelector,
        dataContentTypesSelector,
        dataAllSearchIntentsSelector,
        dataAllKwOpportunitiesSelector,
        dataAllSerpFeaturesSelector,
        filterQuickSettingsSelector,
    ],
    (
        keywordGapResults,
        keywordsInListsMap,
        filterActive,
        filterSettings,
        keywordGapSorting,
        currency,
        urlDataLps,
        relativeKdActive,
        contentTypes,
        searchIntent,
        kwOpportunities,
        serpFeatures,
        quickSettings,
    ) => {
        // Extract keywords from the new data structure
        const data = keywordGapResults.keywords || [];

        // Ensure data is always an array
        if (!Array.isArray(data) || data.length === 0) {
            return [];
        }

        let finalData = data.map((item) => {
            return {
                ...item,
                contentTypes: contentTypes[item.id],
                searchIntent: searchIntent[item.id],
                serpFeatures: serpFeatures[item.id],
                kwOpps: kwOpportunities[item.id],
            }
        })

        // Apply filtering using the same service as competitor keywords
        const filteredData = ResultFilterService.filter({
            data: finalData,
            keywordsInListsMap,
            settings: filterSettings,
            keywordSource: KeywordSourceTypes.KEYWORD_GAP,
            quickSettings,
            filterActive,
            currency,
            selectedSearchVolumeTimeframe: searchVolumeTimeframeTypes.LAST_12_MONTHS,
            urlDataLps,
            relativeKdActive,
        });

        // Apply sorting using the same service as competitor keywords
        finalData = ResultSorterService.sort({
            data: filteredData,
            settings: keywordGapSorting,
            selectedSearchVolumeTimeframe: searchVolumeTimeframeTypes.LAST_12_MONTHS,
        });


        if (filterActive) {
            // We want to filter data after theirs joining, but we don't want to join data before initial filtering
            if (filterSettings.includedContentTypes.length > 0) {
                const filterValues = new Set(filterSettings.includedContentTypes.map(({ value }) => value));

                finalData = finalData.filter((d) => {
                    const hasSearch = filterValues.has("SEARCH") && d.contentTypes === undefined;
                    const hasUnknown = filterValues.has("UNKNOWN") && d.contentTypes?.length === 0;
                    const hasMatchingType = d.contentTypes?.some((type) => filterValues.has(type));

                    return hasSearch || hasUnknown || hasMatchingType;
                });
            }

            if (filterSettings.includedSearchIntentTypes.length !== 0) {
                const filterValues = filterSettings.includedSearchIntentTypes.map(({ value }) => value)
                finalData = finalData.filter((d) =>
                    (d.searchIntent ?? []).some((type) => filterValues.includes(type.toUpperCase()))
                );
            }

            if ((filterSettings.includedKwOpportunities ?? []).length !== 0) {
                const filterValues = new Set(filterSettings.includedKwOpportunities.map(({ value }) => value));
                finalData = finalData.filter((d) => {
                    const hasOops = d.kwOpps !== undefined
                    const opps = d.kwOpps ?? {};
                    const hasSearch = filterValues.has("SEARCH") && !hasOops

                    const hasNoneOpps = hasOops && filterValues.has(KW_OPPORTUNITIES_TYPE.NONE) && !opps.isOutdatedPage && (opps.missingWordsInTitle ?? []).length === 0
                    const hasOutdated = hasOops && filterValues.has(KW_OPPORTUNITIES_TYPE.OUTDATED) && opps.isOutdatedPage
                    const hasMissingWordsInTitle = hasOops && filterValues.has(KW_OPPORTUNITIES_TYPE.MISSING_WORDS) && (opps.missingWordsInTitle ?? []).length !== 0

                    return (
                        hasSearch ||
                        hasMissingWordsInTitle ||
                        hasOutdated ||
                        hasNoneOpps
                    );
                });
            }

            if (filterSettings.includedSerpFeatures.length > 0) {
                const filterValues = new Set(filterSettings.includedSerpFeatures.map(({ value }) => value));

                finalData = finalData.filter((d) => {
                    const hasSearch = filterValues.has("SEARCH") && d.serpFeatures === undefined;
                    const hasNone = filterValues.has("NONE") && d.serpFeatures?.length === 0;
                    const hasMatchingType = Array.isArray(d?.serpFeatures) && d.serpFeatures.some((type) => filterValues.has(type));

                    const result = hasSearch || hasNone || hasMatchingType;
                    return result
                });
            }
        }

        return finalData;
    },
);

// Keyword Gap Results load more availability
export const isMoreKeywordGapResultsAvailableSelector = createSelector(
    [
        keywordGapResultsDataSelector,
        fetchingKeywordGapResultsDataSelector,
        fetchingLoadMoreKeywordGapResultsDataSelector,
        keywordGapResultsErrorSelector,
    ],
    (data, fetching, fetchingLoadMore, error) => {
        // Don't allow load more if currently fetching, has error, or no data
        if (fetching || fetchingLoadMore || error.status || !data || data.length === 0) {
            return false;
        }

        // For now, assume more data is available if we have some data
        // This can be enhanced later with actual pagination info from API
        return data.length > 0;
    },
);

export const filteredAndSortedSelectedKeywordsKeywordGapSelector = createSelector(
    [selectedKeywordIdsSelector, filteredAndSortedKeywordGapResultsDataSelector],
    (ids, sortedAndFilteredKeywords) => sortedAndFilteredKeywords.filter(kw => includes(kw.id, ids)),
);

export const filteredAndSortedSelectedKeywordsKeywordGapSearchCountTotalSelector = createSelector(
    [filteredAndSortedSelectedKeywordsKeywordGapSelector],
    keywords => {
        return reduce(
            (count, { searchVolumesByTimeframe }) => {
                const selectedSearchVolumeTimeframeCount = prop(
                    searchVolumeTimeframeTypes.LAST_12_MONTHS,
                    searchVolumesByTimeframe,
                );

                if (selectedSearchVolumeTimeframeCount > 0) {
                    return count + selectedSearchVolumeTimeframeCount;
                } else {
                    return count;
                }
            },
            0,
            keywords,
        );
    },
);

export const filteredAndSortedSelectedKeywordsKeywordGapAvgCpcSelector = createSelector(
    [filteredAndSortedSelectedKeywordsKeywordGapSelector],
    keywords => {
        const withCpc = keywords.filter(kw => kw.cpc > 0);
        const cpcs = withCpc.map(kw => kw.cpc);

        if (withCpc.length > 0) {
            return sum(cpcs) / withCpc.length;
        } else {
            return 0;
        }
    },
);

export const filteredAndSortedSelectedKeywordsKeywordGapAvgSeoDiffSelector = createSelector(
    [filteredAndSortedSelectedKeywordsKeywordGapSelector],
    keywords => {
        const withRank = keywords.filter(kw => kw.rank > 0);
        const ranks = withRank.map(kw => kw.rank);

        if (withRank.length > 0) {
            return sum(ranks) / withRank.length;
        } else {
            return 0;
        }
    },
);

export const filteredAndSortedSelectedKeywordsKeywordGapAvgPpcSelector = createSelector(
    [filteredAndSortedSelectedKeywordsKeywordGapSelector],
    keywords => {
        const withPpc = keywords.filter(kw => kw.ppc > 0);
        const ppcs = withPpc.map(kw => kw.ppc);

        if (withPpc.length > 0) {
            return sum(ppcs) / withPpc.length;
        } else {
            return 0;
        }
    },
);

export const filteredAndSortedSelectedKeywordsKeywordGapEtTotalSelector = createSelector(
    [filteredAndSortedSelectedKeywordsKeywordGapSelector],
    keywords => {
        const filteredKeywords = keywords.filter(kw => kw.estimatedTraffic > 0);
        return sum(filteredKeywords.map(kw => kw.estimatedTraffic));
    },
);

export const filteredAndSortedSelectedKeywordsKeywordGapAvgSpSelector = createSelector(
    [filteredAndSortedSelectedKeywordsKeywordGapSelector],
    keywords => {
        const withSp = keywords.filter(kw => kw.serpPosition > 0);
        const values = withSp.map(kw => kw.serpPosition);

        if (withSp.length > 0) {
            return sum(values) / withSp.length;
        } else {
            return 0;
        }
    },
);

export const isAllowedPaginationKeywordGapSelector = createSelector(
    [keywordGapResultsSelector],
    data => data.isAllowedPagination
);

export const hasMoreKeywordGapKeywordsAvailableSelector = createSelector(
    [keywordGapResultsSelector],
    (keywordGapResults) => {
        if (!keywordGapResults?.data) {
            return false;
        }
        return Object.values(keywordGapResults.data).some(
            (entry) => Array.isArray(entry?.keywords) && entry.keywords.length < (entry.totalCount ?? 0)
        );
    }
);

// export const filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursKeywordGapSelector = createSelector(
//     [selectedKeywordIdsSelector, filteredAndSortedKeywordGapResultsDataSelector],
//     (selectedIds, filteredAndSortedData) => {
//         const keywordsIds = filteredAndSortedData
//             .filter(keyword => {
//                 const updatedAtMilis = keyword.rankUpdatedAt * 1000;
//                 const gmtNoon = new Date().setUTCHours(0, 0, 0, 0);
//                 return updatedAtMilis < gmtNoon;
//             })
//             .map(item => item.id);

//         const selectedIdsObj = reduce(
//             (obj, val) => {
//                 obj[val] = true; // eslint-disable-line no-param-reassign
//                 return obj;
//             },
//             {},
//             selectedIds,
//         );

//         return keywordsIds.filter(id => selectedIdsObj[id] !== undefined);
//     },
// );


export const filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursSelector = createSelector(
    [selectedKeywordIdsSelector, filteredAndSortedResultsDataSelector],
    (selectedIds, filteredAndSortedData) => {
        const keywordsIds = filteredAndSortedData
            .filter(keyword => {
                const updatedAtMilis = keyword.rankUpdatedAt * 1000;
                const gmtNoon = new Date().setUTCHours(0, 0, 0, 0);
                return updatedAtMilis < gmtNoon;
            })
            .map(item => item.id);

        const selectedIdsObj = reduce(
            (obj, val) => {
                obj[val] = true; // eslint-disable-line no-param-reassign
                return obj;
            },
            {},
            selectedIds,
        );

        return keywordsIds.filter(id => selectedIdsObj[id] !== undefined);
    },
);

export const filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursKeywordGapSelector = createSelector(
    [selectedKeywordIdsSelector, filteredAndSortedKeywordGapResultsDataSelector],
    (selectedIds, filteredAndSortedData) => {
        const keywordsIds = filteredAndSortedData
            .filter(keyword => {
                const updatedAtMilis = keyword.rankUpdatedAt * 1000;
                const gmtNoon = new Date().setUTCHours(0, 0, 0, 0);
                return updatedAtMilis < gmtNoon;
            })
            .map(item => item.id);

        const selectedIdsObj = reduce(
            (obj, val) => {
                obj[val] = true; // eslint-disable-line no-param-reassign
                return obj;
            },
            {},
            selectedIds,
        );

        return keywordsIds.filter(id => selectedIdsObj[id] !== undefined);
    },
);

export const allSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursSelector = createSelector(
    [filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursSelector, filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursKeywordGapSelector],
    (filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHours, filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursKeywordGap) => {
        return [...filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHours, ...filteredAndSortedSelectedKeywordIdsWithRankUpdatedNotInLastTwentyFourHoursKeywordGap];
    },
);

export const allFilteredAndSortedSelectedKeywordIdsSelector = createSelector(
    [selectedKeywordIdsSelector, filteredAndSortedResultsDataSelector, filteredAndSortedKeywordGapResultsDataSelector],
    (selectedIds, filteredAndSortedData, filteredAndSortedKeywordGapResultsDataSelector) => {
        const keywordsIds = [...filteredAndSortedData, ...filteredAndSortedKeywordGapResultsDataSelector].map(item => item.id);

        const selectedIdsObj = reduce(
            (obj, val) => {
                obj[val] = true; // eslint-disable-line no-param-reassign
                return obj;
            },
            {},
            selectedIds,
        );

        return keywordsIds.filter(id => selectedIdsObj[id] !== undefined);
    },
);
